import { useAppSelector, useTranslation, useViewMediaModal } from "@next-platform/client-common-core";
import { SubCapabilityKey, useApplicationCapabilitiesAllowed } from "@next-platform/client-common-core/infra/application/capabilities";
import { Modal, ModalSize } from "@next-platform/client-common-spicy-ui";
import { memo, useCallback, useMemo } from "react";

import MediaFileDetails from "@/pages/Gallery/components/MediaFileDetails/MediaFileDetails";
import getShouldBlurNsfwAvatars from "@/utils/getShouldBlurNsfwAvatars";

const MODAL_CLASS_NAMES = { content: "flex-1 min-h-0 overflow-y-auto !p-0", close: "max-lg:!hidden" };
// One desktop size for every state, so nothing moves while a file or its image loads: the panel is as tall as
// the viewport allows, and as wide as a generated portrait image (640 by 832) at that height plus the 350px
// sidebar. A wider image letterboxes inside that room rather than widening the panel.
const MODAL_CLASS_NAME =
  "!py-0 !h-[100dvh] !w-screen !max-h-none !max-w-none !rounded-none lg:!h-[calc(100dvh-120px)] lg:!max-h-[1020px] lg:!inset-x-0 lg:!translate-x-0 lg:!mx-auto lg:!w-[calc(min(1020px,100dvh-120px)*640/832+350px)] lg:!max-w-[calc(100vw-120px)] lg:!rounded-lg";
// The views before and instead of a file fill the same panel.
const PLACEHOLDER_CLASS_NAME = "h-full w-full";

type MediaFileDetailsModalContentProps = {
  readonly fileId: string;
  readonly testId: string;
};

/**
 * Everything the modal needs while it is open, and nothing while it is not.
 *
 * It lives apart from the gate above it because the gate stays mounted for the life of the app: the reader's
 * settings alone are a couple of dozen store subscriptions, and reading them on every render of every page,
 * to answer a question that only matters once a file is on screen, is work the app never gets back.
 */
function MediaFileDetailsModalContent({ fileId, testId }: MediaFileDetailsModalContentProps) {
  const { t } = useTranslation();
  const { isOpen, position, hasNext, hasPrevious, close, next, previous } = useViewMediaModal();
  // Stepping is its own capability; without it the modal shows one file at a time.
  const isNavigationAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.GalleryMediaNavigation);
  // Two fields, not the settings hook: that one subscribes to twenty-odd selectors for the two read here.
  const isNsfwBlurred = useAppSelector((state) => state.userSettings.blur_nsfw);
  const isNsfwShown = useAppSelector((state) => state.userSettings.show_nsfw);
  const isNsfwUserToggleAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.NsfwUserToggle);
  // Two settings hide adult content: "show NSFW" off means never show it uncovered, and "blur NSFW" is the
  // softer preference for a reader who does see it. Turning the first off clears the second, so both are read.
  const shouldBlurNsfw = !isNsfwShown || getShouldBlurNsfwAvatars({ isNsfwUserToggleAllowed, blurNSFW: isNsfwBlurred });
  // A chat decided per bubble whether to blur each of its files before opening one, so whichever file a step
  // lands on is shown the way its own bubble showed it. A gallery tile or a link decided nothing, and the
  // details view then applies its own per-file rule.
  const isBlurredByOpener = useAppSelector((state) => state.viewMediaModal.blurVerdicts[fileId]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        close();
      }
    },
    [close],
  );

  // The controls are drawn over the image by the details component itself, so both platforms get the
  // same treatment and this modal supplies only the behaviour and the copy. A file opened on its own has
  // no list to step through, and no controls: the shared view reads a missing prop as "hide".
  const navigation = useMemo(
    () =>
      position === null || !isNavigationAllowed
        ? undefined
        : {
            hasNext,
            hasPrevious,
            onNext: next,
            onPrevious: previous,
            nextLabel: t("mediaFile:details.next", { defaultValue: "Next" }),
            previousLabel: t("mediaFile:details.previous", { defaultValue: "Previous" }),
            positionLabel: `${position.current}/${position.total}`,
          },
    [hasNext, hasPrevious, next, previous, position, isNavigationAllowed, t],
  );

  return (
    <Modal
      open={isOpen}
      onOpenChange={handleOpenChange}
      size={ModalSize.FULLSCREEN}
      handleClose={close}
      data-testid={testId}
      // The details pane scrolls itself; the frame stays put so the image and the close control do not
      // scroll away with it.
      className={MODAL_CLASS_NAME}
      classNames={MODAL_CLASS_NAMES}
    >
      <MediaFileDetails
        id={fileId}
        navigation={navigation}
        onClose={close}
        closeLabel={t("mediaFile:details.close", { defaultValue: "Close" })}
        placeholderClassName={PLACEHOLDER_CLASS_NAME}
        shouldBlurNsfw={shouldBlurNsfw}
        isBlurredByOpener={isBlurredByOpener}
        data-testid={`${testId}-Details`}
      />
    </Modal>
  );
}

export default memo(MediaFileDetailsModalContent);
